import * as React from "react";
import chroma from "chroma-js";
import { Block } from "baseui/block";
import { KIND } from "./constants";
import { maybe } from "../utils/functional";
import { mergeOverrides } from "baseui";
import { UnstyledLink } from "../Link";
import { useStyletron } from "kanopi-components/theme/useStyletron";
import { useTrackEvent } from "../tracking";
// The below is temporary and should be removed on next file change.
/* eslint-disable react/prop-types */

import {
  Button as BaseButton,
  SIZE,
  SHAPE,
  ButtonProps as BaseButtonProps,
} from "baseui/button";

// TODO: Rebuild.

export type TButtonProps = {
  NORMAL_WIDTH_RESET?: boolean; // Ignore this until the Button is rebuilt.
  [key: string]: TODO;
  onBlur?: (...args: unknown) => void;
  outlined?: boolean;
} & BaseButtonProps;

export function Button(props: TButtonProps) {
  const {
    kind,
    shape,
    disabled,
    wide,
    arrow,
    color,
    name,
    onClick,
    onBlur,
    href,
    overrides,
    NORMAL_WIDTH_RESET,
    outlined = false,
    ...restProps
  } = props;
  const trackClick = getTrackClick({ name, href, children: restProps.children });
  const [, theme] = useStyletron();
  const baseStyle = getButtonStyle({
    theme,
    disabled,
    kind,
    wide,
    color,
    shape,
    NORMAL_WIDTH_RESET,
  });
  let endEnhancer;
  if (arrow) {
    // eslint-disable-next-line react/display-name
    endEnhancer = () => (
      <Arrow
        color={baseStyle.color}
        wide={wide}
        kind={kind}
        theme={theme}
        disabled={disabled}
      />
    );
  }
  const buttonOverrides = {
    BaseButton: {
      style: baseStyle,
    },
  };
  const mergedOverrides = mergeOverrides(buttonOverrides, overrides);

  return outlined ? (
    <OutlinedButton {...props} />
  ) : (
    // TODO: not wrap all buttons in an <a>
    <UnstyledLink
      href={href}
      style={{
        position: "initial",
        display: "contents",
      }}
    >
      <BaseButton
        type="button"
        overrides={mergedOverrides}
        kind={kind}
        shape={shape}
        endEnhancer={endEnhancer}
        onClick={(...args) => {
          trackClick();
          maybe(onClick)(...args);
          // onBlur and value are changed in the same event loop. This will cause onBluer
          // to be called with the old value. This is a workaround to make sure onBlur is
          // called with the new value.
          setTimeout(() => {
            maybe(onBlur)();
          }, 0);
        }}
        disabled={disabled}
        name={name}
        {...restProps}
      />
    </UnstyledLink>
  );
}

function Arrow(props) {
  const { kind, wide, theme, disabled } = props;
  let color =
    props.color ||
    {
      [KIND.secondary]: theme.colors.contentPrimary,
      [KIND.tertiary]: theme.colors.contentPrimary,
    }[kind] ||
    theme.colors.contentInversePrimary ||
    theme.colors.contentPrimary;
  const width = wide ? "0px" : "25px";
  if (disabled) {
    color = theme.colors.buttonDisabledText;
  }
  return (
    <Block width={width} height="17px">
      <Block display="flex" alignItems="center" position="absolute" right="15px">
        <svg width="25px" height="17px" viewBox="0 0 25 17" version="1.1">
          <g
            id="White-Quote-Flow"
            stroke="none"
            strokeWidth="1"
            fill="none"
            fillRule="evenodd"
          >
            <g
              id="quote8a"
              transform="translate(-835.000000, -749.000000)"
              fill={color}
              fillRule="nonzero"
            >
              <g id="Group-2" transform="translate(835.000000, 750.000000)">
                <polygon
                  id="Line-4-Copy-2"
                  points="15.2932151 0.0213315377 15.9786685 -0.70678495 24.7310587 7.53276988 15.9757298 15.7066901 15.2933099 14.9757298 23.268 7.529"
                ></polygon>
                <polygon
                  id="Line-3-Copy-2"
                  points="23.9782609 7 23.9782609 8 0.0217391304 8 0.0217391304 7"
                ></polygon>
              </g>
            </g>
          </g>
        </svg>
      </Block>
    </Block>
  );
}

function getButtonStyle({
  theme,
  disabled,
  kind,
  shape,
  wide,
  color,
  NORMAL_WIDTH_RESET,
}) {
  const colorStyle = getButtonColorsStyle(theme, disabled, color);
  return {
    position: "relative",
    ...colorStyle,
    ...getButtonKindStyle(theme, kind),
    ...getButtonWidths(theme, shape, wide, NORMAL_WIDTH_RESET),
  };
}

function getButtonKindStyle(theme, kind) {
  let kindStyles = getDisabledStyles({ theme, kind });
  if (kind === KIND.tertiary) {
    kindStyles = {
      ...kindStyles,
      textDecoration: "underline",
      textTransform: "uppercase",
    };
  }
  return kindStyles;
}

function getDisabledStyles({
  theme,
  kind,
  backgroundColor: setBackgroundColor = null,
  color: setColor = null,
}) {
  let backgroundColor = setBackgroundColor;
  let color = setColor;
  if (!(color && backgroundColor)) {
    switch (kind) {
      case KIND.primary:
        (color = color || theme.colors.buttonPrimaryText),
          (backgroundColor = backgroundColor || theme.colors.buttonPrimaryFill);
        break;
      case KIND.secondary:
        (color = color || theme.colors.buttonSecondaryText),
          (backgroundColor = backgroundColor || theme.colors.buttonSecondaryFill);
        break;
      case KIND.tertiary:
        (color = color || theme.colors.buttonTertiaryText),
          (backgroundColor = backgroundColor || theme.colors.buttonTertiaryFill);
        break;
      default:
        break;
    }
  }
  if (backgroundColor && color) {
    return {
      ":disabled": {
        color: chroma(color).alpha(0.4).hex(),
        backgroundColor:
          backgroundColor === "transparent"
            ? backgroundColor
            : chroma(backgroundColor).alpha(0.4).hex(),
      },
    };
  }
}

function getButtonWidths(theme, shape, wide, NORMAL_WIDTH_RESET) {
  if (NORMAL_WIDTH_RESET) {
    return {
      minWidth: "unset",
      paddingLeft: theme.sizing.scale700,
      paddingRight: theme.sizing.scale700,
    };
  } else if (shape === SHAPE.circle) {
    return {
      minWidth: "unset",
    };
  } else {
    return {
      minWidth: wide ? "280px" : theme.sizing.scale3200,
      [theme.mediaQuery.medium]: {
        minWidth: wide ? "330px" : theme.sizing.scale3200,
      },
    };
  }
}

function getButtonColorsStyle(theme, disabled, color) {
  let styles: TODO = {};
  if (color) {
    color = theme.colors[color] || color;
    const chromaColor = chroma(color);
    const bright = isBright(chromaColor);
    styles = {
      backgroundColor: color,
      ":hover": getButtonHoverStyle(disabled, bright, chromaColor),
    };
    if (bright) {
      styles.color = theme.colors.contentPrimary;
    }
  }
  return styles;
}

function getButtonHoverStyle(disabled, bright, chromaColor) {
  const style = {};
  if (!disabled) {
    style.backgroundColor = bright
      ? chromaColor.darken(0.5).hex()
      : chromaColor.brighten(0.7).hex();
  }
  return style;
}

function isBright(color) {
  return chroma(color).get("lab.l") > 75;
}

export function getTrackClick({ name = "Button", href = "", children = "" }) {
  return useTrackEvent({
    event: "Button Clicked",
    properties: { name, href, copy: children.toString() },
  });
}

Button.KIND = KIND;
Button.SIZE = SIZE;
Button.SHAPE = SHAPE;

const OutlinedButton: React.FC<BaseButtonProps> = ({
  disabled = false,
  overrides,
  kind,
  ...restProps
}) => {
  const [, theme] = useStyletron();
  const buttonStyles = {
    [KIND.primary]: {
      BaseButton: {
        style: {
          color: theme.colors.buttonPrimaryOutlinedText,
          outline: `${theme.colors.buttonPrimaryOutlinedBorder} 1px solid`,
          backgroundColor: theme.colors.buttonPrimaryOutlinedFill,
          ":disabled": {
            color: theme.colors.contentStateDisabled,
            backgroundColor: theme.colors.backgroundStateDisabled,
          },
          ":hover": {
            backgroundColor: theme.colors.buttonPrimaryOutlinedHoverFill,
            color: theme.colors.buttonPrimaryOutlinedHoverText,
            outline: "none",
          },
          ":disabled:hover": {
            backgroundColor: theme.colors.backgroundStateDisabled,
            color: theme.colors.buttonPrimaryOutlinedDisabledText,
            outline: `${theme.colors.buttonPrimaryOutlinedBorder} 1px solid`,
          },
          ...theme.typography.LabelSmall,
        },
      },
    },
    [KIND.secondary]: {
      BaseButton: {
        style: {
          color: theme.colors.buttonSecondaryOutlinedText,
          backgroundColor: "transparent",
          ":disabled": {
            color: theme.colors.buttonSecondaryOutlinedDisabledText,
            backgroundColor: "transparent",
          },
          ":hover": {
            backgroundColor: theme.colors.buttonSecondaryOutlinedHoverFill,
            color: theme.colors.buttonSecondaryOutlinedHoverText,
            outline: `${theme.colors.buttonPrimaryOutlinedBorder} 1px solid`,
          },
          ":disabled:hover": {
            backgroundColor: "transparent",
            color: theme.colors.buttonSecondaryOutlinedDisabledText,
            outline: "none",
          },
          ...theme.typography.ParagraphXSmall,
        },
      },
    },
    [KIND.tertiary]: {},
  };
  const buttonStyle = buttonStyles[kind] || buttonStyles["primary"];
  return (
    <BaseButton
      overrides={mergeOverrides(
        {
          ...buttonStyle,
        },
        overrides
      )}
      disabled={disabled}
      kind={kind}
      {...restProps}
    />
  );
};
